import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../Redux/hooks";

const EXPLICIT_LOGOUT_KEY = "explicit-logout";
const POST_LOGIN_REDIRECT_KEY = "post-login-redirect";

/** Call on a user-initiated logout so the next sign-in starts fresh. */
export const markExplicitLogout = () => {
  try {
    sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, "1");
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {}
};

export const clearExplicitLogout = () => {
  try {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  } catch {}
};

/** Only same-app paths are allowed as post-login redirects. */
export const safeRedirect = (redirect: string | null) =>
  redirect && redirect.startsWith("/") && !redirect.startsWith("//")
    ? redirect
    : null;

// After a logout nobody should land back on the page that was open. Interview
// URLs are cut back to the portal landing so a participant is never reopened
// from a login link.
const loginPathFor = (location: {
  pathname: string;
  search: string;
  hash: string;
}) => {
  let explicitLogout = false;
  try {
    explicitLogout = !!sessionStorage.getItem(EXPLICIT_LOGOUT_KEY);
  } catch {}
  if (explicitLogout) return "/auth/login";
  const portal = location.pathname.match(/^\/studies\/[^/]+\/interview(?=\/|$)/);
  const target = portal
    ? portal[0]
    : location.pathname + location.search + location.hash;
  return `/auth/login?redirect=${encodeURIComponent(target)}`;
};

type Props = {
  children?: JSX.Element | JSX.Element[];
  //Pass any array of roles that have access to the route
  disallowedRoles?: string[];
  allowedRoles?: string[];
};
type StudyProps = {
  children?: JSX.Element | JSX.Element[];
  //Pass any array of roles that have access to the route
  action?: string;
  feature?: string;
};

type PlatformProps = {
  children?: JSX.Element | JSX.Element[];
  action?: string;
};

export const AuthenticatedRoute: React.FC<Props> = ({
  children,
  allowedRoles,
  disallowedRoles,
}) => {
  const { isUserAuthenticated, role } = useAppSelector((state) => state.user);
  const location = useLocation();

  if (!isUserAuthenticated) {
    return <Navigate to={loginPathFor(location)} />;
  }

  if (disallowedRoles && disallowedRoles.includes(role)) {
    return <Navigate to="/app/studies" />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/app/studies" />;
  }

  return <>{children}</>;
};

export const UnAuthenticatedRoute: React.FC<any> = ({ children }) => {
  const isUserAuthenticated = useAppSelector(
    (state) => state.user.isUserAuthenticated
  );
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = safeRedirect(params.get("redirect"));

  return isUserAuthenticated ? (
    <Navigate to={redirect || "/app/studies"} />
  ) : (
    <>{children}</>
  );
};

export const StudyAuthenticatedRoute: React.FC<StudyProps> = ({
  children,
  action,
  feature,
}) => {
  const { isUserAuthenticated } = useAppSelector((state) => state.user);
  const { studyPermissions } = useAppSelector((state) => state.study);
  const features = useAppSelector((state) => state.study.features);
  const location = useLocation();
  if (!isUserAuthenticated) {
    return <Navigate to={loginPathFor(location)} />;
  }

  if (action && !studyPermissions.includes(action)) {
    return <Navigate to="/app/studies" />;
  }

  if (feature && !features?.[feature]) {
    return <Navigate to="/app/studies" />;
  }

  return <>{children}</>;
};

export const PlatformAuthenticatedRoute: React.FC<PlatformProps> = ({
  children,
  action,
}) => {
  const { isUserAuthenticated, platformPermissions } = useAppSelector(
    (state) => state.user
  );

  if (!isUserAuthenticated) {
    return <Navigate to="/auth/login" />;
  }
  if (action && !platformPermissions.includes(action)) {
    return <Navigate to="/app/studies" />;
  }

  return <>{children}</>;
};
